import { useCallback, useEffect, useRef } from "react";
import { PatientCrs } from "../types/patientConversationAliasing";
import { Conversation } from "../solverHooks/useConversation";
import {
  ConversationRecommendationFragment,
  QualifiedFeatureFragment,
} from "fe-common/graphql";
import { GenderValue } from "shared";
import { ClusterWithCr } from "../solverHooks/useConversationDD";
import {
  ConversationDriverExchange,
  ConversationStatus,
  DriverDebugData,
  ExchangeAnswer,
} from "../driver/types/driverTypes";
import { useConversationDriver } from "../driver/ConversationDriverProvider";
import { useWidgetSettings } from "../widget/useWidgetSettings";
import { PatientRoute } from "../PatientRoutes/PatientRoute";
import { useClinicData } from "../clinic/clinicHooks";
import { usePatientRouteChanger } from "../PatientRoutes/routeChanger";
import { dispatchKahunEvent, KahunApiEvents } from "../events/events";
import { createPatientState } from "./patientStateManager";
import { DemoPaneStateFields } from "./stateManagers/demoPaneStateManager";
import { isProduction } from "fe-common/utils/envGetter";
import { usePatientFeatureFlags } from "../hooks/usePatientFeatureFlags";
import { getCurrentUserEmail } from "user-management/src/cognito/kahunUserAuth";
import { useLocaleContext } from "fe-common/locale/localeContext";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";

export interface ConversationDDResult {
  ddClusters: ClusterWithCr[];
  crs: PatientCrs[];
  urgency: string;
  navigationRecommendation: ConversationRecommendationFragment;
  options?: any;
}

const SKIP_AGREE_ON_TERMS_USERS = ["michal@kahun.com", "avi@kahun.com"];

export type PatientComputedFields = {
  chiefComplaint: QualifiedFeatureFragment;
  age: number;
  exchanges: ConversationDriverExchange[];
  progress: number;
  conversation: Conversation;
  caseId?: string;
  // Computed filed based on the context, that is turned on when prompt minimum information
  // threshold was set in conversation settings, and end of chat question was suggested,
  // so we know to show the skip conversation button
  // Additionally if empty info message was sent (end message via protocol) turn this flag on
  wasEndOfChatPromptSuggested: boolean;
  // Similar to wasEndOfChatPromptSuggested, this computed is turned on only if currently displayed
  // question is the end of chat question, so we know not to show the skip conversation button
  isEndChatQuestionCurrentlyDisplayed: boolean;
};

export interface PatientStateFields extends DemoPaneStateFields {
  computed: PatientComputedFields;
  stateExchanges: ConversationDriverExchange[];
  onAnswer: (answer: ExchangeAnswer) => void;
  conversationDDResult: {
    ddClusters: { top: ClusterWithCr[]; others: ClusterWithCr[] };
    crs: PatientCrs[];
    urgency: string;
    navigationRecommendation: ConversationRecommendationFragment;
  };
  loading: boolean;
  loadingUndo: boolean;
  masterLoading: boolean;
  setMasterLoading: (loading: boolean) => void;
  undo: () => void;
  setConversationDDResult: (conversationDDResult: ConversationDDResult) => void;
  completeInfoMessages: () => void;
  shareId?: string;
  agreeOnTerms: boolean;
  setAgreeOnTerms: (agreedOnTerms: boolean) => void;
  setAbandoned: (immediate: boolean) => Promise<void>;
  conversationStatus: ConversationStatus;
  onRetry: () => void;
  setConversationDebug: (conversationDebug: DriverDebugData) => void;
  elementIndex: number;
  conversationDebug: Conversation;
  gender: GenderValue;
  showFeedbackPopup: boolean;
  setShowFeedbackPopup: (showPopup: boolean) => void;
  finishConversation: (skipRouting?: boolean) => void;
}

const useQueryParams = () => new URLSearchParams(window.location.search);
export const AGREE_ON_TERMS_STORAGE = "AgreeOnTerms";

function useLazyRef(init) {
  // not initialized yet
  const ref = useRef(null);
  // if it’s not initialized (1st render)
  if (ref.current === null) {
    // we initialize it
    ref.current = init();
  }
  // new we return the initialized ref
  return ref;
}

export const useCreatePatientStateProvider: (_sessionId?: string) => any = (
  _sessionId = null,
) => {
  const sessionId = useRef(_sessionId);

  let {
    partnerId,
    patientCaseId: widgetPatientCaseId,
    onCompletionUrl,
    onAbandonUrl,
    nonPersist,
    termsAgreed,
  } = useWidgetSettings();

  const onCompletionUrlFeatureFlag = usePatientFeatureFlags(
    "onCompletionUrl",
  ) as string;
  const onAbandonUrlFeatureFlag = usePatientFeatureFlags(
    "onAbandonUrl",
  ) as string;

  onCompletionUrl = onCompletionUrl || onCompletionUrlFeatureFlag;
  onAbandonUrl = onAbandonUrl || onAbandonUrlFeatureFlag;

  const queryParams = useQueryParams();

  const driver = useConversationDriver();

  const { clinicSettings } = useClinicData();

  const routeChanger = usePatientRouteChanger();

  const finishConversation = useCallback(
    (skipRouting = false) => {
      const status = driver.getConversationStatus();

      dispatchKahunEvent(KahunApiEvents.status, {
        status,
        progress: driver.getCurrentExchange()?.question?.progress,
      });

      const hasRedirect = onAbandonUrl || onCompletionUrl;
      if (!skipRouting) {
        if (hasRedirect) {
          evt({
            category: CATEGORY.PATIENT_CHAT,
            action: ACTION.REDIRECTED,
            props: {
              status,
              toUrl:
                status === ConversationStatus.ABANDONED
                  ? onAbandonUrl || onCompletionUrl
                  : onCompletionUrl,
            },
            sendImmediately: true,
          });
          if (status === ConversationStatus.ABANDONED) {
            window.location.href = onAbandonUrl || onCompletionUrl;
          } else {
            window.location.href = onCompletionUrl;
          }
        } else {
          routeAfterFinish();
        }
      }

      function routeAfterFinish() {
        if (
          // If it's a new clinic without any settings yet,
          // also show thank you page
          clinicSettings?.showThankYouPage
        ) {
          routeChanger(PatientRoute.Thanks);
        } else if (clinicSettings?.showSummary) {
          routeChanger(PatientRoute.Summary);
        } else {
          // Disclaimer will direct to results
          routeChanger(PatientRoute.Disclaimer);
        }
      }
    },
    [onCompletionUrl, routeChanger, clinicSettings, driver, onAbandonUrl],
  );

  const caseId = queryParams.get("patientCaseId") || widgetPatientCaseId;
  const shareId = queryParams.get("shareId");
  const { clinic: clinicId, name: clinicName } = useClinicData();
  const { locale } = useLocaleContext();
  const prevLocale = useRef(locale);
  const lang = locale || queryParams.get("lang");

  const isDebug = usePatientFeatureFlags("debugSheet") as boolean;

  const agreeOnTerms =
    termsAgreed ||
    (queryParams.get("autosave") && !isProduction()) ||
    !!sessionStorage.getItem(sessionId.current + "_" + AGREE_ON_TERMS_STORAGE);

  const useStoreRef = useLazyRef(
    createPatientState({
      isDebug,
      driver,
      finishConversation,
      sessionId: sessionId.current,
      caseId,
      partnerId,
      clinicId,
      clinicName, // TODO: get clinic name
      lang,
      conversationId: shareId,
      agreeOnTerms,
      nonPersist,
    }),
  );

  const useStore = useStoreRef.current;

  const initState = useStore((state) => state.init);

  useEffect(() => {
    const checkIfUserCanSkipTermsAndInit = async () => {
      try {
        const user = await getCurrentUserEmail();
        if (
          SKIP_AGREE_ON_TERMS_USERS.some((manager) => user === manager) &&
          !isProduction()
        ) {
          initState(true, prevLocale.current !== locale);
        } else {
          initState(termsAgreed, prevLocale.current !== locale);
        }
      } catch (e) {
        initState(termsAgreed, prevLocale.current !== locale);
      }
      prevLocale.current = locale;
    };
    checkIfUserCanSkipTermsAndInit();
  }, [initState, termsAgreed, locale, caseId]);

  return useStore;
};
